/*
  KL scroll row: a dependency-free replacement for small Swiper rows
  (scent + pack-size rows on the DS v1 PDP, DS landing page and Travel Pack).
  Base CSS + this script are included by snippets/kl-scroll-row-assets.liquid.

  The row is a plain CSS scroll-snap strip, so it renders, swipes and
  scrolls before any JS runs (no waiting on custom.js / Swiper, no layout
  jump). This file only adds the desktop arrows and keeps the selected card
  in view.

  Markup:
    <div data-scroll-row>                       (position: relative)
      <button data-scroll-row-prev>
      <div data-scroll-row-track>                (overflow-x: auto; scroll-snap)
        <div>item</div> ...                       (direct children = items)
      </div>
      <button data-scroll-row-next>
    </div>

  State is written as attributes on [data-scroll-row] for CSS to use:
    data-overflow   the track is wider than its viewport
    data-at-start   scrolled to the start (hide prev arrow)
    data-at-end     scrolled to the end   (hide next arrow)
*/
(function () {
  if (window.KLScrollRow) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DURATION = 300; // ms, same as the old Swiper `speed`

  // Animated horizontal scroll. Done with requestAnimationFrame instead of
  // `scrollTo({ behavior: 'smooth' })` because native smooth scrolling is
  // unreliable on scroll-snap containers in some Chromium builds (it can stall
  // at 0), and this gives the same 300ms ease-out everywhere. Snap is paused
  // while animating and restored after; touch / wheel input cancels it.
  function scrollToX(track, x, animate) {
    var max = Math.max(0, track.scrollWidth - track.clientWidth);
    x = Math.max(0, Math.min(x, max));
    if (track.__klAnim) track.__klAnim.cancel();
    var from = track.scrollLeft;
    if (!animate || reduceMotion || Math.abs(x - from) < 1) {
      track.scrollLeft = x;
      return;
    }
    var start = null;
    var raf = 0;
    var anim = {
      cancel: function () {
        cancelAnimationFrame(raf);
        track.style.scrollSnapType = '';
        track.__klAnim = null;
      }
    };
    track.__klAnim = anim;
    track.style.scrollSnapType = 'none';
    var step = function (now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / DURATION);
      var eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      track.scrollLeft = from + (x - from) * eased;
      if (t < 1) raf = requestAnimationFrame(step);
      else anim.cancel();
    };
    raf = requestAnimationFrame(step);
  }

  // The direct child of the track that contains `el`.
  function itemOf(track, el) {
    while (el && el.parentElement !== track) el = el.parentElement;
    return el;
  }

  // Selected item: a checked radio (DS scent / pack rows) or a link marked
  // aria-current="page" (Travel Pack scent tiles, which link to sibling products).
  function checkedItem(track) {
    var checked = track.querySelector('input[type="radio"]:checked, [aria-current="page"]');
    return checked ? itemOf(track, checked) : null;
  }

  function updateState(row, track) {
    var max = track.scrollWidth - track.clientWidth;
    row.toggleAttribute('data-overflow', max > 1);
    row.toggleAttribute('data-at-start', track.scrollLeft <= 1);
    row.toggleAttribute('data-at-end', track.scrollLeft >= max - 1);
  }

  // Scroll horizontally (only) just enough to show `item` in full. Targets
  // are card start edges (the snap points), otherwise scroll-snap would pull
  // the row back and leave the card half cut off.
  function reveal(track, item, animate) {
    if (!item || !track.clientWidth) return;
    var left = item.offsetLeft;
    var right = left + item.offsetWidth;
    var view = track.scrollLeft;
    var target = null;
    if (left < view) {
      target = left;
    } else if (right > view + track.clientWidth) {
      var max = track.scrollWidth - track.clientWidth;
      var need = right - track.clientWidth;
      target = max;
      for (var el = track.firstElementChild; el; el = el.nextElementSibling) {
        if (el.offsetLeft >= need) { target = Math.min(el.offsetLeft, max); break; }
      }
    }
    if (target !== null) scrollToX(track, target, animate);
  }

  // Rows marked data-scroll-row-align="selected" (DS pack sizes) start with the
  // selected card at the left edge, so the larger packs after it are in view.
  // Only used when a row first appears (page load, scent switch); clicks keep
  // the minimal reveal above so the row doesn't move under the pointer.
  function alignSelected(row, track) {
    var item = checkedItem(track);
    if (!item || !track.clientWidth) return;
    if (row.getAttribute('data-scroll-row-align') === 'selected') {
      scrollToX(track, item.offsetLeft, false);
    } else {
      reveal(track, item, false);
    }
  }

  function initRow(row) {
    if (row.__klScrollRow) return;
    var track = row.querySelector('[data-scroll-row-track]');
    if (!track) return;
    row.__klScrollRow = true;

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        updateState(row, track);
      });
    }, { passive: true });

    // Arrows move one card (to the next / previous card's start edge), like
    // the old Swiper navigation.
    row.querySelectorAll('[data-scroll-row-prev], [data-scroll-row-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var back = btn.hasAttribute('data-scroll-row-prev');
        var pos = track.__klAnim ? track.__klTarget : track.scrollLeft;
        var target = back ? 0 : track.scrollWidth;
        for (var el = track.firstElementChild; el; el = el.nextElementSibling) {
          if (back && el.offsetLeft < pos - 1) target = el.offsetLeft;
          if (!back && el.offsetLeft > pos + 1) { target = el.offsetLeft; break; }
        }
        track.__klTarget = Math.max(0, Math.min(target, track.scrollWidth - track.clientWidth));
        scrollToX(track, track.__klTarget, true);
      });
    });

    // Any direct user scrolling wins over an in-flight arrow animation.
    var cancelAnim = function () { if (track.__klAnim) track.__klAnim.cancel(); };
    track.addEventListener('pointerdown', cancelAnim, { passive: true });
    track.addEventListener('wheel', cancelAnim, { passive: true });
    track.addEventListener('touchstart', cancelAnim, { passive: true });

    // Selecting a half-visible card (click, keyboard arrows, or custom.js
    // syncing the radio) brings it fully into view. Capture phase, because
    // custom.js dispatches `new Event('change')` without bubbles.
    track.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') reveal(track, itemOf(track, e.target), true);
    }, true);

    // Pack rows for other scents are display:none until their scent is picked.
    // A ResizeObserver catches "became visible" (0 -> real width) and any
    // viewport resize, so arrows and the selected card are always correct
    // without polling or MutationObservers.
    var wasHidden = true;
    var onSize = function () {
      var hidden = track.clientWidth === 0;
      if (!hidden) {
        if (wasHidden) alignSelected(row, track);
        updateState(row, track);
      }
      wasHidden = hidden;
    };
    if ('ResizeObserver' in window) {
      new ResizeObserver(onSize).observe(track);
    } else {
      onSize();
      window.addEventListener('resize', onSize);
    }

    row.setAttribute('data-scroll-row-ready', '');
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-scroll-row]').forEach(initRow);
  }

  // custom.js sometimes sets `.checked` with no event at all (e.g. the size
  // fallback after a scent switch), so also re-check every visible row once
  // the variant has settled.
  document.addEventListener('variant:change', function () {
    setTimeout(function () {
      document.querySelectorAll('[data-scroll-row-ready]').forEach(function (row) {
        var track = row.querySelector('[data-scroll-row-track]');
        if (track && track.clientWidth) reveal(track, checkedItem(track), true);
      });
    }, 0);
  });

  window.KLScrollRow = { init: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
